import { selectSavedView, selectSavedViewLoading } from 'modules/messagingInsights/selectors';
import { SavedViewParams } from 'modules/messagingInsights/types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CUSTOM_RANGE } from 'ui/datePicker/helpers';
import { isObject } from 'utils/helpers';
import { useGetCurrSettings } from './useGetCurrSettings';

const orderInsensitiveKeys = [
  'selectedMediums',
  'types',
  'states',
  'campaignIds',
  'messageTypeIds',
  'labelIds',
  'workflowIds',
];

/** Returns whether two objects are different */
export const areObjsDiff = (a: any, b: any): boolean => {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  const sameKeys = aKeys.length === bKeys.length && aKeys.every((k, i) => k === bKeys[i]);
  if (!sameKeys) {
    return true;
  } else {
    return bKeys.reduce<boolean>((acc, k: string) => {
      if (isObject(b[k])) {
        return areObjsDiff(a[k], b[k]) || acc;
      }

      /**
       * Do not compare startTime and endTime when the view is using relative date ranges
       */
      if ((k === 'startTime' || k === 'endTime') && b.timePreset !== CUSTOM_RANGE) {
        return false;
      }
      /**
       * For some keys like mediumMetrics + overallMetrics, where the UI does change based on the
       * ordering of the values, we still want to consider 2 arrays with the same values
       * but different orderings as an unsaved change (i.e. ['TotalSent', 'TotalBounces'] !== ['TotalBounce', 'TotalSent']).
       *
       * For those listed in `orderInsensitiveKeys`, the UI is independent of the value order,
       * so updating ['Blast', 'Triggered'] to ['Triggered', 'Blast'] should NOT be considered an unsaved change.
       * We therefore sort those arrays before checking for equality.
       */
      const orderInsensitive = orderInsensitiveKeys.includes(k);
      const aVal = orderInsensitive ? [...a[k]].sort() : a[k];
      const bVal = orderInsensitive ? [...b[k]].sort() : b[k];
      if (JSON.stringify(aVal) !== JSON.stringify(bVal)) {
        return true;
      }
      return acc;
    }, false as boolean);
  }
};

export const useGetUnsavedSettings = () => {
  const viewLoading = useSelector(selectSavedViewLoading);
  const view = useSelector(selectSavedView) as SavedViewParams;
  const currSettings = useGetCurrSettings();

  const unsavedSettings = useMemo(() => {
    if (!viewLoading && view) {
      const { settings } = view;

      return areObjsDiff(settings, currSettings);
    }
    return false;
  }, [viewLoading, view, JSON.stringify(currSettings)]);

  return unsavedSettings;
};
